import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';
import { SeoHead } from '@/components/SeoHead';


interface PrivacyPolicyProps {
  onBack: () => void;
}

export function PrivacyPolicy({ onBack }: PrivacyPolicyProps) {
  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="Privacy Policy — Sportara"
        description="How Sportara collects, uses, and protects your information."
        path="/privacy"
      />

      <header className="sticky top-0 z-10 bg-secondary/95 backdrop-blur-md border-b border-border/10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} className="shrink-0" aria-label="Go back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Logo className="w-6 h-6" alt="Sportara" />
            <span className="font-display font-bold text-lg">Privacy Policy</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 prose prose-sm dark:prose-invert prose-headings:font-display">
        <h1 className="font-display font-bold text-3xl mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground text-sm">Last updated: March 19, 2026</p>

        <h2>1. Information We Collect</h2>
        <p>We collect the following types of information:</p>
        <ul>
          <li><strong>Account Information:</strong> Email address, display name, and authentication credentials when you create an account.</li>
          <li><strong>Profile Data:</strong> Preferred sport, knowledge level, learning goals, and favorite team.</li>
          <li><strong>Usage Data:</strong> Lesson progress, quiz answers, XP earned, streaks, and achievements.</li>
          <li><strong>Device Information:</strong> Browser type, operating system, and general location for analytics purposes.</li>
        </ul>

        <h2>2. How We Use Your Information</h2>
        <p>We use collected information to:</p>
        <ul>
          <li>Provide, maintain, and improve the Service</li>
          <li>Personalize your learning experience and content recommendations</li>
          <li>Track progress and display leaderboard rankings</li>
          <li>Send important service updates and notifications</li>
          <li>Process payments for premium subscriptions</li>
          <li>Analyze usage patterns to improve our platform</li>
        </ul>

        <h2>3. Information Sharing</h2>
        <p>We do not sell your personal information. We may share data with:</p>
        <ul>
          <li><strong>Service Providers:</strong> Third-party services that help us operate the platform (e.g., hosting, payment processing, analytics).</li>
          <li><strong>Leaderboards:</strong> Your display name and progress statistics may be visible to other users on leaderboards.</li>
          <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority.</li>
        </ul>

        <h2>4. Data Security</h2>
        <p>
          We implement industry-standard security measures to protect your data, including encryption in transit and at rest, secure authentication, and regular security reviews. However, no method of transmission over the internet is 100% secure.
        </p>

        <h2>5. Data Retention</h2>
        <p>
          We retain your data for as long as your account is active or as needed to provide the Service. You may request deletion of your account and associated data at any time by contacting us.
        </p>

        <h2>6. Your Rights</h2>
        <p>Depending on your location, you may have the right to:</p>
        <ul>
          <li>Access, correct, or delete your personal information</li>
          <li>Object to or restrict processing of your data</li>
          <li>Request a copy of your data in a portable format</li>
          <li>Withdraw consent at any time</li>
        </ul>

        <h2>7. Cookies and Tracking</h2>
        <p>
          We use essential cookies to maintain your session and preferences. We may use analytics tools to understand how the Service is used. You can control cookie settings through your browser.
        </p>

        <h2>8. Third-Party Services</h2>
        <p>
          The Service may contain links to third-party websites or integrate with third-party services (e.g., Stripe for payments). We are not responsible for the privacy practices of these third parties.
        </p>

        <h2>9. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of material changes via the Service or email. Your continued use of the Service after changes constitutes acceptance.
        </p>

        <h2>10. Contact Us</h2>
        <p>
          For questions about this Privacy Policy, contact us at <a href="mailto:appsportara@gmail.com" className="text-primary hover:underline">appsportara@gmail.com</a>.
        </p>
      </main>
    </div>
  );
}
